import { AlertTriangle } from "lucide-react";
import { fmt } from "@/lib/api";

const PHASE_LABEL = { single: "Single phase", three: "Three phase", unknown: "Unknown" };

/**
 * Supply type + meter channel count always shows; the L1/L2/L3 breakdown
 * and imbalance warning only render for a three-phase device with actual
 * phase telemetry (usage.phase_diagnostics/usage.live.phases) — a
 * single-phase or not-yet-configured device just shows the summary line.
 */
export default function PhaseCard({ device, usage }) {
  const diag = usage.phase_diagnostics;
  const livePhases = usage.live?.phases;
  const isThree = device.phase_type === "three";

  return (
    <div className="border border-kl-lineLight p-6 animate-rise" data-testid="phase-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="kl-label">Electrical supply</p>
          <h3 className="font-display text-xl mt-1">{PHASE_LABEL[device.phase_type] || "Unknown"}</h3>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-widest text-kl-mutedLight border border-kl-lineLight px-2 py-1" data-testid="phase-meter-channels">
          {device.meter_channels ?? "—"} meter channel{device.meter_channels === 1 ? "" : "s"}
        </span>
      </div>

      {isThree && diag && diag.imbalanced && (
        <div className="mt-4 flex items-start gap-2 border border-yellow-500 bg-yellow-50 text-yellow-800 p-3 text-sm" data-testid="phase-imbalance-warning">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>Phase imbalance detected — about {diag.imbalance_pct}% difference between the busiest and lightest phase.</span>
        </div>
      )}

      {isThree && (livePhases || diag) && (
        <table className="w-full text-sm mt-4" data-testid="phase-table">
          <thead>
            <tr className="kl-label text-left">
              <th className="py-2 font-normal">Phase</th>
              <th className="py-2 font-normal text-right">Voltage</th>
              <th className="py-2 font-normal text-right">Current</th>
              <th className="py-2 font-normal text-right">Power</th>
              <th className="py-2 font-normal text-right">Energy</th>
              <th className="py-2 font-normal text-right">Peak</th>
            </tr>
          </thead>
          <tbody>
            {(livePhases || diag.peak_w && Object.keys(diag.peak_w).map((phase) => ({ phase }))).map((p, i) => {
              const key = p.phase || `L${i + 1}`;
              return (
                <tr key={key} className="kl-row">
                  <td className="py-2 font-mono">{key}</td>
                  <td className="py-2 text-right font-mono">{p.voltage != null ? `${fmt.num(p.voltage, 1)} V` : "—"}</td>
                  <td className="py-2 text-right font-mono">{p.current != null ? `${fmt.num(p.current, 2)} A` : "—"}</td>
                  <td className="py-2 text-right font-mono">{p.power_w != null ? `${fmt.num(p.power_w, 0)} W` : "—"}</td>
                  <td className="py-2 text-right font-mono">{diag?.energy_kwh?.[key] != null ? `${fmt.num(diag.energy_kwh[key], 2)} kWh` : "—"}</td>
                  <td className="py-2 text-right font-mono">{diag?.peak_w?.[key] != null ? `${fmt.num(diag.peak_w[key], 0)} W` : "—"}</td>
                </tr>
              );
            })}
            <tr className="kl-row font-medium">
              <td className="py-2 font-mono">Aggregate</td>
              <td className="py-2 text-right font-mono">{usage.live ? `${fmt.num(usage.live.voltage, 1)} V` : "—"}</td>
              <td className="py-2 text-right font-mono">{usage.live ? `${fmt.num(usage.live.current, 2)} A` : "—"}</td>
              <td className="py-2 text-right font-mono">{usage.live ? `${fmt.num(usage.live.power_w, 0)} W` : "—"}</td>
              <td className="py-2 text-right font-mono">{fmt.num(usage.total_kwh, 2)} kWh</td>
              <td className="py-2 text-right font-mono">{fmt.num(usage.peak_kw * 1000, 0)} W</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
